import {
  removeSharedMember,
  fetchSharedMembers,
  updateSharedMemberPermission,
} from '../../api.js';

import { ANIMATED_BACKGROUNDS, buildThemeColors, extractFontOverrides } from '../SettingsModal.jsx';

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// #RGB, #RRGGBB, #RRGGBBAA
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const NAMED_HEX = {
  white: '#FFFFFF',
  black: '#000000',
  red: '#FF0000',
  green: '#008000',
  blue: '#0000FF',
  yellow: '#FFFF00',
  gray: '#808080',
  grey: '#808080',
  orange: '#FFA500',
  purple: '#800080',
  pink: '#FFC0CB',
  brown: '#A52A2A',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
};

function pickFirst(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return undefined;
}

function capitalizeFirst(s) {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function normKey(s) {
  return (s || '').toString().trim().toLowerCase();
}

function resolveFolderTitle(action = {}) {
  return pickFirst(action.title, action.folderTitle, action.name, action.newTitle);
}

function toHexIfNamedColor(v) {
  if (typeof v !== 'string') return v;
  const s = v.trim().toLowerCase();
  return NAMED_HEX[s] || v;
}

function assertHexColor(value, keyName = 'color') {
  if (value == null) return;
  if (typeof value !== 'string' || !HEX_COLOR_RE.test(value.trim())) {
    throw new Error(`Invalid ${keyName}: "${value}". Use hex only (e.g. #RRGGBB).`);
  }
}

function normalizeHex(value) {
  return typeof value === 'string' ? value.trim().toUpperCase() : value;
}

async function waitForFolder(dataRef, title, parentId = null, maxWait = 5000) {
  const key = title.trim().toLowerCase();
  const interval = 200;
  let waited = 0;

  while (waited < maxWait) {
    const match = Object.values(dataRef.current).find((f) => {
      if (f.id === 'root') return false;
      if (f.title?.trim().toLowerCase() !== key) return false;
      if (parentId && f.parentId !== parentId) return false;
      return true;
    });

    if (match) return match.id;
    await delay(interval);
    waited += interval;
  }
  return null;
}

async function waitForFolderById(dataRef, id, maxWait = 5000) {
  const interval = 200;
  let waited = 0;

  while (waited < maxWait) {
    if (dataRef.current[id]) return id;
    await delay(interval);
    waited += interval;
  }
  return null;
}

function resolveToDataRefKey(dataRef, id, title) {
  const currentData = dataRef.current;

  if (id && currentData[id]) return id;

  if (id) {
    const realId = id.startsWith('shared_sub_')
      ? id.replace('shared_sub_', '')
      : id.startsWith('shared_')
      ? id.replace('shared_', '')
      : id;

    const byReal = Object.values(currentData).find(
      (f) => f._realFolderId === realId || f._dbId === realId || f.id === realId
    );
    if (byReal) return byReal.id;
  }

  if (title) {
    const key = title.trim().toLowerCase();
    const byTitle = Object.values(currentData).find(
      (f) => f.id !== 'root' && f.title?.trim().toLowerCase() === key
    );
    if (byTitle) return byTitle.id;
  }

  return null;
}

function folderExistsInParent(dataRef, parentId, title) {
  if (!title || typeof title !== 'string') return false;
  const key = title.trim().toLowerCase();
  if (!key) return false;

  return Object.values(dataRef.current).some(
    (f) =>
      f.id !== 'root' &&
      f.parentId === parentId &&
      f.title?.trim().toLowerCase() === key
  );
}

function normalizeUrl(url) {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname + u.search + u.hash).toLowerCase().replace(/\/+$/, '');
  } catch (_) {
    return (url || '').trim().toLowerCase().replace(/\/+$/, '');
  }
}

function toAbsoluteUrl(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function isValidWebUrl(url) {
  try {
    const u = new URL(url);
    return !!u.hostname;
  } catch {
    return false;
  }
}

function bookmarkExistsInFolder(dataRef, folderId, title, url) {
  const folder = folderId === 'root' ? dataRef.current.root : dataRef.current[folderId];
  if (!folder) return false;

  const titleKey = title?.trim().toLowerCase();
  const normalizedNew = url ? normalizeUrl(url) : '';

  return (folder.items || []).some((item) => {
    if (item.type !== 'link') return false;
    if (normalizedNew && item.url && normalizeUrl(item.url) === normalizedNew) return true;
    if (titleKey && item.title?.trim().toLowerCase() === titleKey) return true;
    return false;
  });
}

function getAllBookmarks(dataRef) {
  return Object.values(dataRef.current).flatMap((f) =>
    (f.items || []).filter((i) => i.type === 'link')
  );
}

function getFolderByKey(dataRef, folderKey) {
  return folderKey === 'root' ? dataRef.current.root : dataRef.current[folderKey];
}

function getBookmarksInFolder(dataRef, folderKey) {
  const folder = getFolderByKey(dataRef, folderKey);
  if (!folder) return [];
  return (folder.items || []).filter((i) => i.type === 'link');
}

function bookmarkMatchesHint(bookmark, hint) {
  if (!bookmark || !hint || typeof hint !== 'string') return false;
  const key = hint.trim().toLowerCase();
  if (!key) return false;

  const t = (bookmark.title || '').trim().toLowerCase();
  const u = (bookmark.url || '').trim().toLowerCase();

  return t === key || t.includes(key) || u.includes(key);
}

function findBookmarkByHint(dataRef, hint, candidates = null) {
  if (!hint || typeof hint !== 'string') return null;
  const pool = Array.isArray(candidates) ? candidates : getAllBookmarks(dataRef);
  return pool.find((b) => bookmarkMatchesHint(b, hint)) || null;
}

export function makeExecuteAction({
  dataRef,
  settingsRef,
  folderRegistryRef,
  addBookmark,
  addFolder,
  editBookmark,
  editFolder,
  removeBookmark,
  removeFolder,
  moveBookmarkToFolder,
  moveFolderToFolder,
  onShareFolder,
  onSettingsChange,
}) {
  function extractFolderHints(action = {}) {
    const folderTitleHint = pickFirst(
      action.folderTitle,
      action.targetFolderTitle,
      action.parentTitle,
      action.inFolder,
      action.in_folder,
      action.folder,
      action.toFolder,
      action.destinationFolder,
      action.destinationFolderTitle
    );

    const folderIdHint = pickFirst(
      action.folderId,
      action.targetFolderId,
      action.parentId,
      action.inFolderId,
      action.in_folder_id,
      action.toFolderId,
      action.destinationFolderId
    );

    return { folderTitleHint, folderIdHint };
  }

  function extractBookmarkHints(action = {}) {
    return {
      idHint: pickFirst(action.id, action.bookmarkId, action.bmId, action.itemId),
      titleHint: pickFirst(
        action.query,
        action.bookmarkTitle,
        action.currentTitle,
        action.oldTitle,
        action.fromTitle,
        action.targetTitle,
        action.name,
        action.title,
        action.url
      ),
    };
  }

  async function resolveFolderKeyAsync(folderId, folderTitle, expectedParentId = null) {
    let key = null;

    if (folderTitle) {
      const tk = folderTitle.trim().toLowerCase();
      if (folderRegistryRef.current[tk]) key = folderRegistryRef.current[tk];
    }

    if (!key && folderId && !String(folderId).startsWith('shared_')) {
      const tk = String(folderId).trim().toLowerCase();
      if (folderRegistryRef.current[tk]) key = folderRegistryRef.current[tk];
    }

    if (!key) key = resolveToDataRefKey(dataRef, folderId, folderTitle);

    if (key) {
      if (!dataRef.current[key]) await waitForFolderById(dataRef, key, 6000);
      return key;
    }

    if (folderTitle) {
      const polledId = await waitForFolder(dataRef, folderTitle, expectedParentId, 6000);
      if (polledId) return polledId;
    }

    return null;
  }

  async function resolveFolderDbIdAsync(folderId, folderTitle) {
    if (folderTitle) {
      const key = await resolveFolderKeyAsync(null, folderTitle);
      if (key) {
        const entry = dataRef.current[key];
        return entry?._realFolderId || entry?._dbId || entry?.id || key;
      }
    }

    if (folderId) {
      const key = await resolveFolderKeyAsync(folderId, null);
      if (key) {
        const entry = dataRef.current[key];
        return entry?._realFolderId || entry?._dbId || entry?.id || key;
      }
    }

    return null;
  }

  async function resolveTargetFolderIdAsync(action) {
    const folderTitle = pickFirst(
      action.targetFolderTitle,
      action.folderTitle,
      action.toFolder,
      action.destinationFolder,
      action.folder,
      action.parentTitle
    );

    const folderId = pickFirst(
      action.targetFolderId,
      action.folderId,
      action.toFolderId,
      action.destinationFolderId,
      action.parentId
    );

    if (folderTitle) {
      const fk = normKey(folderTitle);
      if (fk === 'root' || fk === 'home') return 'root';
      const key = await resolveFolderKeyAsync(null, folderTitle);
      if (key) return key;
      throw new Error(`Cannot find folder "${folderTitle}".`);
    }

    if (folderId) {
      if (folderId === 'root') return 'root';
      const key = await resolveFolderKeyAsync(folderId, null);
      if (key) return key;
      throw new Error(`Cannot find folder with id "${folderId}".`);
    }

    throw new Error('Missing target folder reference (targetFolderId/targetFolderTitle/folderId/folderTitle).');
  }

  function resolveBookmarkRealId(id) {
    if (!id) return null;
    if (typeof id === 'string' && id.startsWith('shared_bm_')) return id.replace('shared_bm_', '');

    for (const folder of Object.values(dataRef.current)) {
      const found = (folder.items || []).find((i) => i.id === id || i._realBookmarkId === id);
      if (found) return found._realBookmarkId || found.id;
    }

    return id;
  }

  async function resolveBookmarkFromActionAsync(action = {}) {
    const { idHint, titleHint } = extractBookmarkHints(action);

    if (idHint) {
      const realId = resolveBookmarkRealId(idHint);
      const byId = getAllBookmarks(dataRef).find(
        (b) => b.id === realId || b._realBookmarkId === realId || b.id === idHint
      );
      if (byId) return byId;
    }

    const { folderTitleHint, folderIdHint } = extractFolderHints(action);

    if (folderTitleHint || folderIdHint) {
      const folderKey = await resolveFolderKeyAsync(folderIdHint, folderTitleHint);
      if (folderKey) {
        const scoped = getBookmarksInFolder(dataRef, folderKey);
        const inFolder = findBookmarkByHint(dataRef, titleHint, scoped);
        if (inFolder) return inFolder;
      }
    }

    if (titleHint) {
      return findBookmarkByHint(dataRef, titleHint);
    }

    return null;
  }

  function normalizeShortcutUrl(value = '') {
    let s = (value || '').trim();
    if (!s) return null;

    s = s.replace(/^(site|website|link|url)\s+/i, '').trim();
    if (!s) return null;

    if (/^https?:\/\//i.test(s)) return s;
    if (/^www\./i.test(s)) return `https://${s}`;

    if (/^[a-z0-9-]+(\.[a-z0-9-]+)+([/:?#].*)?$/i.test(s)) {
      return `https://${s}`;
    }

    const knownSites = {
      jitsi: 'https://meet.jit.si',
      meet: 'https://meet.jit.si',
      youtube: 'https://youtube.com',
      yt: 'https://youtube.com',
      google: 'https://google.com',
      github: 'https://github.com',
      gh: 'https://github.com',
      twitter: 'https://x.com',
      x: 'https://x.com',
      reddit: 'https://reddit.com',
      linkedin: 'https://linkedin.com',
      facebook: 'https://facebook.com',
      fb: 'https://facebook.com',
      instagram: 'https://instagram.com',
      ig: 'https://instagram.com',
      netflix: 'https://netflix.com',
      spotify: 'https://spotify.com',
      amazon: 'https://amazon.com',
      chatgpt: 'https://chatgpt.com',
      claude: 'https://claude.ai',
      gmail: 'https://mail.google.com',
      twitch: 'https://twitch.tv',
      chess: 'https://chess.com',
      lichess: 'https://lichess.org',
      discord: 'https://discord.com',
      whatsapp: 'https://web.whatsapp.com',
      telegram: 'https://web.telegram.org',
      notion: 'https://notion.so',
      figma: 'https://figma.com',
    };

    const lower = s.toLowerCase();
    if (knownSites[lower]) return knownSites[lower];

    if (/^[a-z0-9-]+$/i.test(s)) {
      return `https://${s}.com`;
    }

    return null;
  }

  function tryOpenUrl(url) {
    try {
      // Avoid passing 'noopener' feature string directly to window.open because it forces
      // modern browsers to return null even when the window is opened successfully!
      const win = window.open(url, '_blank');
      if (win) {
        try {
          win.opener = null; // Cleanly disown opener reference for security post-launch
        } catch (_) {}
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  }

  return async function executeAction(action) {
    switch (action.action) {
      case 'open_bookmark': {
        let finalUrl = null;
        let resolvedTitle = action.title || action.name;

        // If action already has a valid URL specified, trust the URL!
        if (action.url) {
          finalUrl = toAbsoluteUrl(action.url);
          // If action has an id, verify if that id matches the same URL; if not, discard hallucinated ID
          if (action.id) {
            const bm = getAllBookmarks(dataRef).find((b) => b.id === action.id || b._realBookmarkId === action.id);
            if (bm && bm.url && normalizeUrl(bm.url) === normalizeUrl(finalUrl)) {
              resolvedTitle = bm.title;
              action.title = bm.title;
            } else {
              action.id = null; // Discard mismatched hallucinated ID
            }
          }
        } else {
          const bm = await resolveBookmarkFromActionAsync(action);
          if (bm) {
            finalUrl = toAbsoluteUrl(bm?.url);
            action.id = bm.id;
            action.title = bm.title;
            resolvedTitle = bm.title;
          }
        }

        if (!finalUrl) {
          const fallbackHint = pickFirst(action.title, action.query, action.name);
          const candidate = normalizeShortcutUrl(fallbackHint);
          if (candidate) {
            finalUrl = candidate;
            if (!action.title) action.title = capitalizeFirst(fallbackHint);
            resolvedTitle = action.title;
          }
        }

        if (!finalUrl || !isValidWebUrl(finalUrl)) {
          throw new Error(`Bookmark URL not found or invalid: "${pickFirst(action.title, action.query, action.url) || ''}"`);
        }

        action.url = finalUrl;
        const opened = tryOpenUrl(finalUrl);
        action.opened = opened;

        const displayTitle = resolvedTitle || action.title || (function() {
          try { return new URL(finalUrl).hostname.replace(/^www\./, ''); } catch (_) { return 'Link'; }
        })();

        if (!opened) {
          action.result = `⚠️ Browser pop-up blocked. Tap the button above or [click here to launch "${displayTitle}"](${finalUrl}) ↗`;
        } else {
          action.result = `🚀 Opened ["${displayTitle}"](${finalUrl}) in a new tab.`;
        }
        break;
      }

      case 'get_credentials': {
        const bm = await resolveBookmarkFromActionAsync(action);
        if (!bm) {
          const hint = pickFirst(action.title, action.name, action.query, action.id, action.bookmarkId, action.bmId);
          throw new Error(`Cannot find bookmark for credentials: "${hint}"`);
        }

        action.id = bm.id;
        action.title = bm.title;
        action.url = bm.url;
        action.credentials = bm.credentials || '';
        action.bookmark = bm;
        action.result = bm.credentials?.trim()
          ? `🔐 Vault Credential for "${bm.title}" is ready. Tap the secure vault button to view safely.`
          : `No credentials saved for "${bm.title}".`;
        break;
      }

      case 'get_note': {
        const bm = await resolveBookmarkFromActionAsync(action);
        if (!bm) {
          const hint = pickFirst(action.title, action.name, action.query, action.id, action.bookmarkId, action.bmId);
          throw new Error(`Cannot find bookmark for note: "${hint}"`);
        }

        action.id = bm.id;
        action.title = bm.title;
        action.note = bm.note || '';
        action.result = bm.note?.trim()
          ? `Note for "${bm.title}": ${bm.note}`
          : `No note saved for "${bm.title}"`;
        break;
      }

      case 'list_bookmarks': {
        const { folderTitleHint, folderIdHint } = extractFolderHints(action);

        let items = [];
        let folderLabel = 'all folders';

        if (folderTitleHint || folderIdHint) {
          const folderKey = await resolveFolderKeyAsync(folderIdHint, folderTitleHint);
          if (!folderKey) {
            throw new Error(`Cannot find folder "${folderTitleHint || folderIdHint}".`);
          }

          const folder = getFolderByKey(dataRef, folderKey);
          if (!folder) {
            throw new Error(`Cannot find folder "${folderTitleHint || folderIdHint}".`);
          }

          folderLabel = folder.title || 'Home';
          items = getBookmarksInFolder(dataRef, folderKey);
          action.folderId = folder.id;
          action.folderTitle = folder.title || action.folderTitle || 'Home';
        } else {
          items = getAllBookmarks(dataRef);
        }

        action.result = items.length
          ? items.map((b) => `- ${b.title} — ${b.url}`).join('\n')
          : `No bookmarks found in ${folderLabel}.`;
        break;
      }

      case 'add_bookmark': {
        let folderId = 'root';
        const { folderTitleHint, folderIdHint } = extractFolderHints(action);

        if (folderTitleHint) {
          const fk = normKey(folderTitleHint);
          if (fk === 'root' || fk === 'home') {
            folderId = 'root';
          } else {
            const key = await resolveFolderKeyAsync(null, folderTitleHint);
            if (key) folderId = key;
          }
        } else if (folderIdHint) {
          if (folderIdHint === 'root') {
            folderId = 'root';
          } else {
            const key = await resolveFolderKeyAsync(folderIdHint, null);
            if (key) folderId = key;
          }
        }

        action.folderId = folderId;
        if (!action.folderTitle && folderId === 'root') action.folderTitle = 'Home';

        const absUrl = toAbsoluteUrl(action.url) || action.url;
        if (!absUrl || !isValidWebUrl(absUrl)) {
          throw new Error(`Invalid URL for add_bookmark: "${action.url}"`);
        }

        const safeBmTitle = capitalizeFirst(
          action.title || new URL(absUrl).hostname.replace(/^www\./, '')
        );
        action.title = safeBmTitle;

        if (bookmarkExistsInFolder(dataRef, folderId, safeBmTitle, absUrl)) {
          console.warn(
            `[AI] Skipping duplicate bookmark "${safeBmTitle}" — exact match already exists in folder.`
          );
          break;
        }

        let logoUrl = '';
        try {
          logoUrl = `https://www.google.com/s2/favicons?domain=${new URL(absUrl).hostname}&sz=128`;
        } catch (_) {}

        await addBookmark(folderId, {
          title: safeBmTitle,
          url: absUrl,
          logoUrl,
          note: action.note || '',
          credentials: action.credentials || '',
        });
        break;
      }

      case 'delete_bookmark': {
        let bm = null;

        const directId = pickFirst(action.id, action.bookmarkId, action.bmId);
        if (directId) {
          const realId = resolveBookmarkRealId(directId);
          bm = getAllBookmarks(dataRef).find(
            (b) => b.id === realId || b._realBookmarkId === realId || b.id === directId
          ) || null;
        }

        if (!bm) {
          bm = await resolveBookmarkFromActionAsync(action);
        }

        if (!bm) throw new Error('Bookmark not found.');

        await removeBookmark(resolveBookmarkRealId(bm.id));
        action.id = bm.id;
        action.title = bm.title;
        break;
      }

      case 'rename_bookmark': {
        const bm = await resolveBookmarkFromActionAsync(action);
        const nextTitle = capitalizeFirst(
          pickFirst(action.newTitle, action.toTitle, action.name, action.title)
        );

        if (!bm) {
          const hint = pickFirst(
            action.query,
            action.bookmarkTitle,
            action.currentTitle,
            action.oldTitle,
            action.fromTitle,
            action.targetTitle,
            action.name,
            action.title,
            action.id,
            action.bookmarkId,
            action.bmId
          );
          throw new Error(`Cannot find bookmark to rename: "${hint}"`);
        }
        if (!nextTitle) throw new Error('Missing new bookmark title.');

        action.id = bm.id;
        action.oldTitle = bm.title;
        await editBookmark(resolveBookmarkRealId(bm.id), { title: nextTitle });
        action.title = nextTitle;
        break;
      }

      case 'move_bookmark': {
        const bm = await resolveBookmarkFromActionAsync(action);
        if (!bm) {
          const hint = pickFirst(
            action.query,
            action.bookmarkTitle,
            action.currentTitle,
            action.oldTitle,
            action.fromTitle,
            action.targetTitle,
            action.name,
            action.title,
            action.id,
            action.bookmarkId,
            action.bmId
          );
          throw new Error(`Cannot find bookmark to move: "${hint}"`);
        }

        const resolvedFolderId = await resolveTargetFolderIdAsync(action);
        action.id = bm.id;
        action.title = bm.title;
        action.targetFolderId = resolvedFolderId;
        await moveBookmarkToFolder(resolveBookmarkRealId(bm.id), resolvedFolderId);
        break;
      }

      case 'update_note': {
        const note = pickFirst(action.note, action.notes, action.description);
        const bm = await resolveBookmarkFromActionAsync(action);

        if (!bm) {
          const hint = pickFirst(
            action.query,
            action.bookmarkTitle,
            action.currentTitle,
            action.oldTitle,
            action.fromTitle,
            action.targetTitle,
            action.name,
            action.title,
            action.id,
            action.bookmarkId,
            action.bmId
          );
          throw new Error(`Cannot find bookmark for note update: "${hint}"`);
        }

        action.id = bm.id;
        action.title = bm.title;
        await editBookmark(resolveBookmarkRealId(bm.id), { note: note || '' });
        break;
      }

      case 'update_credentials': {
        const credentials = pickFirst(action.credentials, action.credential, action.login);
        const bm = await resolveBookmarkFromActionAsync(action);

        if (!bm) {
          const hint = pickFirst(
            action.query,
            action.bookmarkTitle,
            action.currentTitle,
            action.oldTitle,
            action.fromTitle,
            action.targetTitle,
            action.name,
            action.title,
            action.id,
            action.bookmarkId,
            action.bmId
          );
          throw new Error(`Cannot find bookmark for credentials update: "${hint}"`);
        }

        action.id = bm.id;
        action.title = bm.title;
        await editBookmark(resolveBookmarkRealId(bm.id), { credentials: credentials || '' });
        break;
      }

      case 'update_url': {
        const bm = await resolveBookmarkFromActionAsync(action);
        const nextUrlRaw = pickFirst(action.newUrl, action.link, action.url);

        if (!bm) {
          const hint = pickFirst(
            action.query,
            action.bookmarkTitle,
            action.currentTitle,
            action.oldTitle,
            action.fromTitle,
            action.targetTitle,
            action.name,
            action.title,
            action.id,
            action.bookmarkId,
            action.bmId
          );
          throw new Error(`Cannot find bookmark for URL update: "${hint}"`);
        }

        const abs = toAbsoluteUrl(nextUrlRaw) || nextUrlRaw;
        if (!abs || !isValidWebUrl(abs)) {
          throw new Error(`Invalid URL for update_url: "${nextUrlRaw}"`);
        }

        action.id = bm.id;
        action.title = bm.title;
        await editBookmark(resolveBookmarkRealId(bm.id), { url: abs });
        break;
      }

      case 'update_bookmark': {
        const bm = await resolveBookmarkFromActionAsync(action);
        if (!bm) {
          const hint = pickFirst(
            action.query,
            action.bookmarkTitle,
            action.currentTitle,
            action.oldTitle,
            action.fromTitle,
            action.targetTitle,
            action.name,
            action.title,
            action.id,
            action.bookmarkId,
            action.bmId
          );
          throw new Error(`Cannot find bookmark to update: "${hint}"`);
        }

        const realBmId = resolveBookmarkRealId(bm.id);
        const updates = {};

        const nextUrlRaw = pickFirst(action.newUrl, action.link, action.url);
        const nextTitle = pickFirst(action.newTitle, action.name, action.title);
        const nextNote = pickFirst(action.note, action.notes, action.description);
        const nextCreds = pickFirst(action.credentials, action.credential, action.login);

        if (nextUrlRaw !== undefined) {
          const abs = toAbsoluteUrl(nextUrlRaw) || nextUrlRaw;
          if (!abs || !isValidWebUrl(abs)) {
            throw new Error(`Invalid URL for update_bookmark: "${nextUrlRaw}"`);
          }
          updates.url = abs;
        }
        if (nextTitle !== undefined) updates.title = capitalizeFirst(nextTitle);
        if (nextNote !== undefined) updates.note = nextNote;
        if (nextCreds !== undefined) updates.credentials = nextCreds;

        action.id = bm.id;
        action.title = bm.title;

        if (Object.keys(updates).length > 0) await editBookmark(realBmId, updates);
        break;
      }

      case 'add_folder': {
        const folderTitle = resolveFolderTitle(action);
        if (!folderTitle || typeof folderTitle !== 'string' || !folderTitle.trim()) {
          throw new Error('add_folder requires a valid folder title.');
        }
        const safeTitle = capitalizeFirst(folderTitle.trim());
        action.title = safeTitle;

        const rawParent = pickFirst(
          action.parentId,
          action.parentTitle,
          action.targetParentId,
          action.targetParentTitle
        );
        let parentId = 'root';

        if (rawParent && rawParent !== 'root') {
          const resolvedKey = await resolveFolderKeyAsync(rawParent, rawParent);
          if (resolvedKey) parentId = resolvedKey;
        }

        if (folderExistsInParent(dataRef, parentId, safeTitle)) {
          const existing = Object.values(dataRef.current).find(
            (f) =>
              f.id !== 'root' &&
              f.parentId === parentId &&
              f.title?.trim().toLowerCase() === safeTitle.toLowerCase()
          );
          if (existing) {
            folderRegistryRef.current[safeTitle.toLowerCase()] = existing.id;
            action.id = existing.id;
            action.title = existing.title || safeTitle;
          }
          console.warn(`[AI] Skipping duplicate folder "${safeTitle}" — already exists.`);
          break;
        }

        const newId = await addFolder(parentId, safeTitle);
        if (newId) {
          folderRegistryRef.current[safeTitle.toLowerCase()] = newId;
          action.id = newId;
        } else {
          const polledId = await waitForFolder(dataRef, safeTitle, parentId);
          if (polledId) {
            folderRegistryRef.current[safeTitle.toLowerCase()] = polledId;
            action.id = polledId;
          }
        }
        break;
      }

      case 'delete_folder': {
        const folderHintId = pickFirst(action.id, action.folderId);
        const folderHintTitle = pickFirst(action.title, action.folderTitle, action.name);

        const dataRefKey = await resolveFolderKeyAsync(folderHintId, folderHintTitle);
        if (!dataRefKey) throw new Error(`Cannot find folder to delete: "${folderHintId || folderHintTitle}"`);

        const folderEntry = dataRef.current[dataRefKey];
        if (!action.title && folderEntry?.title) action.title = folderEntry.title;
        if (!action.id) action.id = dataRefKey;

        await removeFolder(dataRefKey);
        break;
      }

      case 'rename_folder': {
        const folderHintId = pickFirst(action.id, action.folderId);
        const folderHintTitle = pickFirst(action.folderTitle, action.title, action.fromTitle, action.name);
        const dataRefKey = await resolveFolderKeyAsync(folderHintId, folderHintTitle);

        const nextTitle = capitalizeFirst(pickFirst(action.newTitle, action.toTitle, action.title));
        if (!nextTitle) throw new Error('Missing new folder title.');

        const keyToUse = dataRefKey || folderHintId;
        if (!keyToUse) throw new Error('Cannot find folder to rename.');
        await editFolder(keyToUse, nextTitle);
        action.title = nextTitle;
        break;
      }

      case 'move_folder': {
        if (!moveFolderToFolder) throw new Error('moveFolderToFolder not available.');

        const sourceFolderKey = await resolveFolderKeyAsync(action.folderId, action.folderTitle);
        if (!sourceFolderKey) throw new Error(`Cannot find folder "${action.folderTitle || action.folderId}".`);

        let targetParentKey;
        if (action.targetParentId === 'root' || action.targetParentTitle?.toLowerCase() === 'root') {
          targetParentKey = 'root';
        } else {
          targetParentKey = await resolveFolderKeyAsync(action.targetParentId, action.targetParentTitle);
          if (!targetParentKey) throw new Error(`Cannot find target folder "${action.targetParentTitle || action.targetParentId}".`);
        }

        await moveFolderToFolder(sourceFolderKey, targetParentKey);
        break;
      }

      case 'bulk_delete': {
        const ids = Array.isArray(action.ids) ? action.ids : [];
        if (!ids.length) throw new Error('bulk_delete requires non-empty ids array.');

        for (const id of ids) {
          if (action.type === 'folder') {
            const dataRefKey = await resolveFolderKeyAsync(id, null);
            await removeFolder(dataRefKey || id);
          } else {
            const realId = resolveBookmarkRealId(id);
            await removeBookmark(realId);
          }
        }
        break;
      }

      case 'share_folder': {
        const inviteeEmail = action.inviteeEmail || action.email || action.invitee || action.memberEmail;
        const permission = action.permission || action.role || 'viewer';

        let resolvedDbId = null;
        const { folderTitleHint, folderIdHint } = extractFolderHints(action);

        if (folderTitleHint) {
          const polledKey = await resolveFolderKeyAsync(null, folderTitleHint);
          if (polledKey) {
            const entry = dataRef.current[polledKey];
            resolvedDbId = entry?._realFolderId || entry?._dbId || entry?.id || polledKey;
          }
        }

        if (!resolvedDbId && folderIdHint) {
          const key = await resolveFolderKeyAsync(folderIdHint, null);
          if (key) {
            const entry = dataRef.current[key];
            resolvedDbId = entry?._realFolderId || entry?._dbId || entry?.id || key;
          }
        }

        if (!resolvedDbId) throw new Error(`Cannot find folder "${folderTitleHint || folderIdHint}" to share.`);
        if (!inviteeEmail) throw new Error('No email provided for sharing.');

        if (onShareFolder) await onShareFolder(resolvedDbId, inviteeEmail, permission);

        action.inviteeEmail = inviteeEmail;
        action.permission = permission;
        break;
      }

      case 'revoke_shared_member': {
        const email = action.inviteeEmail || action.email || action.memberEmail;
        const dbFolderId = await resolveFolderDbIdAsync(action.folderId, action.folderTitle);
        if (!dbFolderId) throw new Error('Cannot find folder.');
        if (!email) throw new Error('No email provided.');

        const members = await fetchSharedMembers(dbFolderId);
        const member = members.find((m) => m.invitee_email?.toLowerCase() === email.toLowerCase());
        if (!member) throw new Error(`${email} is not a member.`);

        await removeSharedMember(member.id);
        action.inviteeEmail = email;
        break;
      }

      case 'update_permission': {
        const email = action.inviteeEmail || action.email || action.memberEmail;
        const permission = action.permission || action.role || 'viewer';

        const dbFolderId = await resolveFolderDbIdAsync(action.folderId, action.folderTitle);
        if (!dbFolderId) throw new Error('Cannot find folder.');
        if (!email) throw new Error('No email provided.');

        const members = await fetchSharedMembers(dbFolderId);
        const member = members.find((m) => m.invitee_email?.toLowerCase() === email.toLowerCase());
        if (!member) throw new Error(`${email} is not a member.`);

        await updateSharedMemberPermission(member.id, permission);
        action.inviteeEmail = email;
        action.permission = permission;
        break;
      }

      case 'leave_shared_folder': {
        const dbFolderId = await resolveFolderDbIdAsync(action.folderId, action.folderTitle);
        if (!dbFolderId) throw new Error('Cannot find folder.');

        const folderEntry = Object.values(dataRef.current).find(
          (f) =>
            f._isShared &&
            (f._dbId === dbFolderId ||
              f.id === dbFolderId ||
              f._realFolderId === dbFolderId ||
              f.targetId === dbFolderId)
        );

        if (!folderEntry) throw new Error('Shared folder not found.');
        if (!folderEntry._memberRecordId) throw new Error('Member record not found.');

        await removeSharedMember(folderEntry._memberRecordId);
        break;
      }

      case 'apply_theme': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;

        const requestedAccent = toHexIfNamedColor(action.customAccent || action.accent);
        if (!requestedAccent) throw new Error('apply_theme requires "customAccent" (hex).');

        assertHexColor(requestedAccent, 'customAccent');
        const accent = normalizeHex(requestedAccent);

        const isDarkMode = current.isDark !== false;
        const fontOverrides = extractFontOverrides(current.themeColors);

        await onSettingsChange({
          ...current,
          theme: 'custom',
          themeColors: buildThemeColors(accent, isDarkMode, {
            ...fontOverrides,
            accent,
            _customFonts: true,
          }),
        });
        break;
      }

      case 'apply_background': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;

        if (action.pageBg || action.backgroundColor || action.bg) {
          const rawBg = toHexIfNamedColor(action.pageBg || action.backgroundColor || action.bg);
          const newPageBg = normalizeHex(rawBg);
          assertHexColor(newPageBg, 'page background color');

          const accent = current.themeColors?.accent || '#C2FFE0';
          const isDarkMode = current.isDark !== false;
          const existing = extractFontOverrides(current.themeColors);

          await onSettingsChange({
            ...current,
            themeColors: buildThemeColors(accent, isDarkMode, {
              ...existing,
              pageBg: newPageBg,
              _customFonts: true,
            }),
          });

          if (!action.backgroundType && !action.customUrl) break;
        }

        const bgTypeRaw = (action.backgroundType || '').trim().toLowerCase();
        const bgAliasesToNone = new Set(['none', 'solid', 'plain', 'off', 'no_animation', 'no-animation']);

        if (bgAliasesToNone.has(bgTypeRaw)) {
          await onSettingsChange({
            ...current,
            backgroundType: 'none',
            customBackground: '',
            backgroundGradient: '',
          });
        } else if (action.customUrl) {
          await onSettingsChange({
            ...current,
            backgroundType: 'custom',
            customBackground: action.customUrl,
            backgroundGradient: '',
          });
        } else if (bgTypeRaw) {
          const bg = ANIMATED_BACKGROUNDS.find((b) => b.id === bgTypeRaw);
          if (!bg) throw new Error(`Unknown animation "${action.backgroundType}".`);

          await onSettingsChange({
            ...current,
            backgroundType: bg.id,
            customBackground: '',
            backgroundGradient: '',
          });
        }

        break;
      }

      case 'apply_mode': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;
        const isDarkMode = action.mode === 'dark';
        const accent = current.themeColors?.accent || '#C2FFE0';
        const fontOverrides = extractFontOverrides(current.themeColors);

        await onSettingsChange({
          ...current,
          isDark: isDarkMode,
          themeColors: buildThemeColors(accent, isDarkMode, fontOverrides),
        });
        break;
      }

      case 'apply_font_colors': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;
        const accentFallback = current.themeColors?.accent || '#C2FFE0';
        const isDarkMode = current.isDark !== false;
        const existing = extractFontOverrides(current.themeColors);

        const candidate = {
          accent: toHexIfNamedColor(action.accent),
          heading: toHexIfNamedColor(action.heading),
          text: toHexIfNamedColor(action.text || action.body),
          muted: toHexIfNamedColor(action.muted || action.subtitle),
          label: toHexIfNamedColor(action.label || action.labels),
          chatUserBg: toHexIfNamedColor(action.chatUserBg),
          chatUser: toHexIfNamedColor(action.chatUser),
          chatAiBg: toHexIfNamedColor(action.chatAiBg),
          chatAi: toHexIfNamedColor(action.chatAi),
          aiButtonBg: toHexIfNamedColor(action.aiButtonBg || action.aiBase),
          aiButtonText: toHexIfNamedColor(action.aiButtonText || action.aiIconColor || action.aiTextColor),
          pageBg: toHexIfNamedColor(action.pageBg || action.backgroundColor || action.background || action.bg),
          card: toHexIfNamedColor(action.card),
          border: toHexIfNamedColor(action.border),
          surface: toHexIfNamedColor(action.surface),
        };

        for (const [k, v] of Object.entries(candidate)) {
          if (v !== undefined) assertHexColor(v, k);
        }

        const normalized = {};
        for (const [k, v] of Object.entries(candidate)) {
          if (v !== undefined) normalized[k] = normalizeHex(v);
        }

        const finalAccent = normalized.accent || accentFallback;

        const newOverrides = {
          ...existing,
          ...normalized,
          _customFonts: true,
        };

        await onSettingsChange({
          ...current,
          theme: 'custom',
          themeColors: buildThemeColors(finalAccent, isDarkMode, newOverrides),
        });
        break;
      }

      case 'reset_font_colors': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;
        const accent = current.themeColors?.accent || '#C2FFE0';
        const isDarkMode = current.isDark !== false;

        await onSettingsChange({
          ...current,
          themeColors: buildThemeColors(accent, isDarkMode, {}),
        });
        break;
      }

      case 'audit_account': {
        const allBm = getAllBookmarks(dataRef);
        const allFolders = Object.values(dataRef.current).filter((f) => f.id !== 'root');
        const rootBm = (dataRef.current['root']?.items || []).filter((i) => i.type === 'link');

        const urlMap = new Map();
        const duplicates = [];
        for (const bm of allBm) {
          if (!bm.url) continue;
          const norm = normalizeUrl(bm.url);
          if (urlMap.has(norm)) {
            duplicates.push({ title: bm.title, url: bm.url, duplicateOf: urlMap.get(norm).title });
          } else {
            urlMap.set(norm, bm);
          }
        }

        const emptyFolders = allFolders.filter((f) => (f.items || []).length === 0);
        const withNotes = allBm.filter((b) => b.note?.trim());
        const withCreds = allBm.filter((b) => b.credentials?.trim());

        action.auditData = {
          totalBookmarks: allBm.length,
          totalFolders: allFolders.length,
          rootCount: rootBm.length,
          duplicatesCount: duplicates.length,
          emptyFoldersCount: emptyFolders.length,
          withNotesCount: withNotes.length,
          withCredsCount: withCreds.length,
        };

        action.result = [
          `📊 **Whole-Account Health Audit Report**`,
          ``,
          `• 📁 **Structure**: ${allFolders.length} Folders, ${allBm.length} Total Bookmarks`,
          `• ⚡ **Root Loose Items**: ${rootBm.length} bookmarks${rootBm.length > 0 ? ' (reply "organize root" to cluster into folders)' : ' (Clean!)'}`,
          `• 🔁 **Duplicate URLs**: ${duplicates.length} detected${duplicates.length > 0 ? ` (e.g. "${duplicates[0].title}")` : ' (Zero duplicates!)'}`,
          `• 🗑️ **Empty Folders**: ${emptyFolders.length} folder(s)${emptyFolders.length > 0 ? ` (e.g. "${emptyFolders[0].title}")` : ' (All populated)'}`,
          `• 📝 **Notes Enriched**: ${withNotes.length} bookmark(s)`,
          `• 🔐 **Vault Secured Accounts**: ${withCreds.length} item(s)`,
          ``,
          `*Your entire library is synchronized with MeMark Cloud.*`,
        ].join('\n');
        break;
      }

      case 'auto_organize_root': {
        const rootItems = [...(dataRef.current['root']?.items || []).filter((i) => i.type === 'link')];
        if (rootItems.length === 0) {
          action.count = 0;
          action.result = '🗂️ Root is already clean! There are no loose bookmarks in root to organize.';
          break;
        }

        const domainClusters = {
          Development: ['github.com', 'gitlab.com', 'stackoverflow.com', 'npmjs.com', 'dev.to', 'codepen.io', 'vercel.com', 'cloudflare.com', 'docker.com'],
          Media: ['youtube.com', 'youtu.be', 'netflix.com', 'spotify.com', 'twitch.tv', 'vimeo.com', 'bilibili.com'],
          Social: ['twitter.com', 'x.com', 'reddit.com', 'linkedin.com', 'facebook.com', 'instagram.com', 'discord.com', 'telegram.org'],
          Design: ['figma.com', 'dribbble.com', 'behance.net', 'canva.com', 'unsplash.com'],
          Reading: ['medium.com', 'wikipedia.org', 'arxiv.org', 'substack.com', 'news.ycombinator.com', 'theverge.com', 'wired.com'],
        };

        let movedCount = 0;
        const createdFolders = new Set();

        for (const item of rootItems) {
          if (!item.url) continue;
          let matchedCategory = 'General';

          try {
            const host = new URL(item.url).hostname.toLowerCase().replace(/^www\./, '');
            for (const [cat, domains] of Object.entries(domainClusters)) {
              if (domains.some((d) => host === d || host.endsWith('.' + d))) {
                matchedCategory = cat;
                break;
              }
            }
          } catch (_) {}

          let targetFolderKey = Object.keys(dataRef.current).find(
            (k) => k !== 'root' && normKey(dataRef.current[k]?.title) === normKey(matchedCategory)
          );

          if (!targetFolderKey) {
            await addFolder(matchedCategory, 'root');
            createdFolders.add(matchedCategory);
            targetFolderKey = await waitForFolder(dataRef, matchedCategory, 'root', 3000);
          }

          if (targetFolderKey && moveBookmarkToFolder) {
            const targetDbId = dataRef.current[targetFolderKey]?._realFolderId || targetFolderKey;
            await moveBookmarkToFolder(item._realBookmarkId || item.id, targetDbId);
            movedCount++;
          }
        }

        action.count = movedCount;
        action.result = `🗂️ **Auto-Organized ${movedCount} Root Bookmarks** into smart clusters: ${Array.from(createdFolders).join(', ') || 'existing folders'}!`;
        break;
      }

      case 'open_folder_tabs': {
        const folderName = pickFirst(action.folderTitle, action.folder, action.title);
        const folderId = pickFirst(action.folderId, action.id);
        let folder = null;

        if (folderId && dataRef.current[folderId]) {
          folder = dataRef.current[folderId];
        } else if (folderName) {
          const key = await resolveFolderKeyAsync(null, folderName);
          if (key) folder = dataRef.current[key];
        }

        if (!folder) {
          throw new Error(`Could not find folder "${folderName || folderId || ''}".`);
        }

        const items = (folder.items || []).filter((i) => i.type === 'link' && i.url);
        if (items.length === 0) {
          action.result = `Folder "${folder.title}" contains no bookmarks.`;
          break;
        }

        let openedCount = 0;
        for (const bm of items) {
          if (tryOpenUrl(bm.url)) openedCount++;
        }

        action.count = items.length;
        action.folderTitle = folder.title;
        action.result = `🚀 **Launched ${openedCount} / ${items.length} Bookmarks** from "${folder.title}" in parallel tabs!`;
        break;
      }

      case 'audit_vault_security': {
        const allBm = getAllBookmarks(dataRef);
        const creds = allBm.filter((b) => b.credentials?.trim());

        if (creds.length === 0) {
          action.result = `🔐 **Zero-Knowledge Vault Security Audit**\n\nNo stored credentials found in your bookmarks. When you save credentials on a bookmark, MeMark secures them client-side in your encrypted vault.`;
          break;
        }

        let weakCount = 0;
        let strongCount = 0;
        let reusedCount = 0;
        const passSet = new Set();

        for (const c of creds) {
          const raw = (c.credentials || '').trim();
          if (raw.length < 8) weakCount++;
          else if (raw.length >= 12 && /[A-Z]/.test(raw) && /[0-9]/.test(raw) && /[^A-Za-z0-9]/.test(raw)) strongCount++;

          if (passSet.has(raw)) reusedCount++;
          else passSet.add(raw);
        }

        action.vaultAudit = {
          total: creds.length,
          weak: weakCount,
          strong: strongCount,
          reused: reusedCount,
        };

        action.result = [
          `🔐 **Zero-Knowledge Vault Security Audit**`,
          ``,
          `• 🛡️ **Total Vault Accounts**: ${creds.length}`,
          `• 💪 **Strong Passwords**: ${strongCount} accounts (12+ chars, mixed symbols & numbers)`,
          `• ⚠️ **Weak Passwords**: ${weakCount} account(s)${weakCount > 0 ? ' (recommend upgrading to 12+ chars)' : ''}`,
          `• 🔁 **Reused Passwords**: ${reusedCount} account(s)`,
          ``,
          `*Your credentials are never sent to or logged by AI models.*`,
        ].join('\n');
        break;
      }

      case 'export_vault': {
        const format = (action.format || 'html').toLowerCase();
        const allBm = getAllBookmarks(dataRef);
        const allFolders = Object.values(dataRef.current);
        const dateStr = new Date().toISOString().split('T')[0];

        let content = '';
        let mime = 'text/html;charset=utf-8';
        let filename = `memark-vault-backup-${dateStr}.html`;

        if (format === 'json') {
          content = JSON.stringify(dataRef.current, null, 2);
          mime = 'application/json';
          filename = `memark-vault-backup-${dateStr}.json`;
        } else if (format === 'markdown' || format === 'obsidian') {
          mime = 'text/markdown;charset=utf-8';
          filename = `memark-vault-backup-${dateStr}.md`;
          content = `# MeMark Vault Export (${dateStr})\n\n`;
          for (const f of allFolders) {
            const items = (f.items || []).filter((i) => i.type === 'link');
            if (items.length === 0) continue;
            content += `## 📁 ${f.title || 'Bookmarks'}\n\n`;
            for (const b of items) {
              content += `- [${b.title || b.url}](${b.url})${b.note ? ` — *${b.note}*` : ''}\n`;
            }
            content += `\n`;
          }
        } else {
          content = [
            `<!DOCTYPE html>`,
            `<html><head><meta charset="utf-8"><title>MeMark Vault Backup</title>`,
            `<style>`,
            `body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: #0A0F1D; color: #E2E8F0; padding: 2rem; max-width: 900px; margin: 0 auto; line-height: 1.6; }`,
            `h1 { color: #00FF9D; border-bottom: 1px solid #1E293B; padding-bottom: 0.5rem; }`,
            `.folder { margin-top: 1.5rem; background: #111827; border-radius: 8px; padding: 1rem 1.5rem; border: 1px solid #1F2937; }`,
            `.folder h2 { color: #38BDF8; margin-top: 0; font-size: 1.2rem; }`,
            `ul { list-style: none; padding-left: 0; }`,
            `li { margin: 0.5rem 0; }`,
            `a { color: #A7F3D0; text-decoration: none; font-weight: 500; }`,
            `a:hover { text-decoration: underline; color: #34D399; }`,
            `.note { color: #94A3B8; font-size: 0.85rem; margin-top: 0.2rem; }`,
            `.footer { margin-top: 2rem; text-align: center; color: #64748B; font-size: 0.8rem; }`,
            `</style></head><body>`,
            `<h1>MeMark Sovereign Vault Export</h1>`,
            `<p>Exported on ${new Date().toLocaleString()} • Encrypted Cloud Synchronized (${allBm.length} Bookmarks)</p>`,
          ].join('\n');

          for (const f of allFolders) {
            const items = (f.items || []).filter((i) => i.type === 'link');
            if (items.length === 0) continue;
            content += `  <div class="folder">\n    <h2>📁 ${f.title || 'Bookmarks'}</h2>\n    <ul>\n`;
            for (const b of items) {
              content += `      <li><a href="${b.url}" target="_blank" rel="noopener noreferrer">${b.title || b.url}</a>`;
              if (b.note) content += `<div class="note">📝 ${b.note}</div>`;
              content += `</li>\n`;
            }
            content += `    </ul>\n  </div>\n`;
          }
          content += `  <div class="footer">Generated by MeMark AI Sovereign Hub • https://app.memark.me</div></body></html>`;
        }

        try {
          const blob = new Blob([content], { type: mime });
          const downloadUrl = URL.createObjectURL(blob);
          action.downloadUrl = downloadUrl;
          action.filename = filename;

          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (_) {}

        action.format = format;
        action.result = `💾 **Vault Export Complete**\n\nGenerated standalone offline backup file \`${filename}\`. Download started automatically!`;
        break;
      }

      case 'get_account_stats': {
        const allBm = getAllBookmarks(dataRef);
        const allFolders = Object.values(dataRef.current).filter((f) => f.id !== 'root');
        const sharedFolders = Object.values(dataRef.current).filter((f) => f._isShared || f._isOwnerShared);

        const domainCounts = {};
        for (const b of allBm) {
          if (!b.url) continue;
          try {
            const host = new URL(b.url).hostname.replace(/^www\./, '');
            domainCounts[host] = (domainCounts[host] || 0) + 1;
          } catch (_) {}
        }
        const topDomains = Object.entries(domainCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([dom, count]) => `${dom} (${count})`);

        action.result = [
          `📈 **MeMark Sovereign Account Telemetry**`,
          ``,
          `• 📚 **Bookmarks Count**: ${allBm.length}`,
          `• 📁 **Active Folders**: ${allFolders.length}`,
          `• 🌐 **Top Domain Clusters**: ${topDomains.join(', ') || 'None'}`,
          `• 🤝 **Shared Workspaces**: ${sharedFolders.length}`,
          `• 📝 **Enriched Notes**: ${allBm.filter((b) => b.note?.trim()).length}`,
          `• 🔒 **Vault Credentials**: ${allBm.filter((b) => b.credentials?.trim()).length}`,
          `• ⚡ **Edge Sync Engine**: Active (Cloudflare Pages + Encrypted DB)`,
        ].join('\n');
        break;
      }

      case 'apply_theme_preset': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;
        const presetKey = (action.preset || action.theme || 'cyberpunk').toLowerCase().trim();

        const PRESET_MAP = {
          cyberpunk: { accent: '#00FF9D', bg: 'matrix', isDark: true },
          matrix: { accent: '#00FF41', bg: 'matrix', isDark: true },
          quantum: { accent: '#00F0FF', bg: 'quantum', isDark: true },
          solarized: { accent: '#B58900', bg: 'none', isDark: true },
          velvet: { accent: '#E040FB', bg: 'nebula', isDark: true },
          emerald: { accent: '#10B981', bg: 'none', isDark: true },
          amber: { accent: '#F59E0B', bg: 'none', isDark: true },
          rose: { accent: '#F43F5E', bg: 'none', isDark: true },
          cobalt: { accent: '#3B82F6', bg: 'none', isDark: true },
        };

        const target = PRESET_MAP[presetKey] || PRESET_MAP.cyberpunk;
        await onSettingsChange({
          ...current,
          isDark: target.isDark,
          backgroundType: target.bg,
          theme: presetKey,
          themeColors: buildThemeColors(target.accent, target.isDark, {}),
        });

        action.preset = presetKey;
        action.result = `🎨 Switched theme to **${presetKey.toUpperCase()}** (Accent: \`${target.accent}\`, Background: \`${target.bg}\`).`;
        break;
      }

      case 'set_view_density': {
        if (!onSettingsChange) throw new Error('Settings not available.');
        const current = settingsRef.current;
        const density = (action.density || 'standard').toLowerCase().trim();
        const validDensities = new Set(['dense', 'standard', 'cinema']);
        const finalDensity = validDensities.has(density) ? density : 'standard';

        await onSettingsChange({
          ...current,
          density: finalDensity,
        });

        action.density = finalDensity;
        action.result = `📐 Grid layout density updated to **${finalDensity.toUpperCase()}**.`;
        break;
      }

      case 'search_library': {
        const allBm = getAllBookmarks(dataRef);
        const q = (action.query || action.keyword || '').trim().toLowerCase();
        const folderFilter = (action.folderTitle || action.folder || '').trim().toLowerCase();

        let matches = allBm;
        if (folderFilter) {
          matches = matches.filter((b) => (b.folderTitle || '').toLowerCase().includes(folderFilter));
        }
        if (q) {
          matches = matches.filter((b) => {
            const t = (b.title || '').toLowerCase();
            const u = (b.url || '').toLowerCase();
            const n = (b.note || '').toLowerCase();
            return t.includes(q) || u.includes(q) || n.includes(q);
          });
        }
        if (action.hasCredentials) {
          matches = matches.filter((b) => b.credentials?.trim());
        }
        if (action.hasNote) {
          matches = matches.filter((b) => b.note?.trim());
        }

        action.matchesCount = matches.length;

        if (matches.length === 0) {
          action.result = `🔍 No bookmarks found matching your search "${q || folderFilter || ''}".`;
        } else {
          const listStr = matches
            .slice(0, 6)
            .map((b) => `• [${b.title || b.url}](${b.url}) ↗${b.credentials ? ' 🔐' : ''}${b.note ? ' 📝' : ''}`)
            .join('\n');
          action.result = `🔍 **Found ${matches.length} matching bookmark(s)**:\n\n${listStr}${matches.length > 6 ? `\n\n*...and ${matches.length - 6} more*` : ''}`;
        }
        break;
      }

      case 'find_duplicates':
      case 'clean_duplicates': {
        const allBm = getAllBookmarks(dataRef);
        const urlMap = new Map();

        const normalizeUrlKey = (raw = '') => {
          try {
            const u = new URL(raw);
            const path = u.pathname.replace(/\/+$/, '') || '/';
            return `${u.protocol}//${u.hostname.toLowerCase()}${path}`;
          } catch (_) {
            return (raw || '').toLowerCase().trim().replace(/\/+$/, '');
          }
        };

        for (const b of allBm) {
          if (!b.url) continue;
          const key = normalizeUrlKey(b.url);
          if (!urlMap.has(key)) urlMap.set(key, []);
          urlMap.get(key).push(b);
        }

        const duplicateGroups = Array.from(urlMap.entries()).filter(([_, list]) => list.length > 1);

        if (duplicateGroups.length === 0) {
          action.duplicateCount = 0;
          action.result = '✨ **Library is pristine!** Zero duplicate bookmark URLs detected across all folders.';
          break;
        }

        let totalDuplicates = 0;
        duplicateGroups.forEach(([_, list]) => {
          totalDuplicates += list.length - 1;
        });

        if (action.action === 'find_duplicates') {
          action.duplicateCount = totalDuplicates;
          const previewList = duplicateGroups
            .slice(0, 5)
            .map(([key, list]) => `• **${list[0].title || key}** (${list.length} copies) — [${key}](${key})`)
            .join('\n');
          action.result = `🔍 **Detected ${totalDuplicates} duplicate bookmark(s) across ${duplicateGroups.length} URLs**:\n\n${previewList}${duplicateGroups.length > 5 ? `\n\n*...and ${duplicateGroups.length - 5} more duplicate groups*` : ''}\n\n*Type \`clean duplicates\` to automatically remove redundant copies while preserving notes and credentials!*`;
          break;
        }

        // Clean duplicates: keep the best one per group, remove redundant ones
        let removedCount = 0;
        for (const [_, list] of duplicateGroups) {
          // Score bookmarks to retain the highest-value one
          const scored = [...list].sort((a, b) => {
            const aScore = (a.credentials ? 10 : 0) + (a.note ? 5 : 0) + (a.tags?.length ? 2 : 0);
            const bScore = (b.credentials ? 10 : 0) + (b.note ? 5 : 0) + (b.tags?.length ? 2 : 0);
            return bScore - aScore;
          });

          const keep = scored[0];
          const toRemove = scored.slice(1);

          for (const item of toRemove) {
            try {
              await removeBookmark(resolveBookmarkRealId(item.id));
              removedCount++;
            } catch (_) {}
          }
        }

        action.duplicateCount = removedCount;
        action.result = `🧹 **Duplicate Cleanup Complete**\n\nSuccessfully removed **${removedCount}** redundant bookmark(s) across **${duplicateGroups.length}** duplicate groups. All notes, tags, and credentials on retained items were safely preserved!`;
        break;
      }

      case 'purge_empty_folders':
      case 'clean_empty_folders': {
        const allFolders = Object.values(dataRef.current).filter((f) => f && f.id !== 'root');
        const allBm = getAllBookmarks(dataRef);

        const emptyFolders = allFolders.filter((f) => {
          const hasBookmarks = (f.items || []).filter((i) => i.type === 'link').length > 0;
          if (hasBookmarks) return false;
          // Check if any bookmark in library has folder_id === f.id
          const hasOrphanBm = allBm.some((b) => b.folderId === f.id || b.folder_id === f.id);
          if (hasOrphanBm) return false;
          // Check if any folder has parent_id === f.id
          const hasChildren = allFolders.some((c) => c.parentId === f.id || c.parent_id === f.id);
          if (hasChildren) return false;
          return true;
        });

        if (emptyFolders.length === 0) {
          action.removedCount = 0;
          action.result = '✨ **Folders are cleanly structured!** Zero empty or abandoned folders detected.';
          break;
        }

        const removedNames = [];
        for (const f of emptyFolders) {
          try {
            await removeFolder(f.id);
            removedNames.push(f.title || 'Untitled');
          } catch (_) {}
        }

        action.removedCount = removedNames.length;
        action.result = `🗑️ **Purged ${removedNames.length} Empty Folder(s)**\n\nCleaned folders: ${removedNames.map((n) => `\`${n}\``).join(', ')}. Your workspace hierarchy is now streamlined!`;
        break;
      }

      case 'batch_tag': {
        const allBm = getAllBookmarks(dataRef);
        let rawTags = action.tags || action.tag || [];
        if (typeof rawTags === 'string') {
          rawTags = rawTags.split(/[\s,]+/).filter(Boolean);
        }
        const cleanedTags = rawTags.map((t) => (t.startsWith('#') ? t : `#${t}`));

        if (cleanedTags.length === 0) {
          throw new Error('Please specify tags to apply, e.g. #dev, #tools.');
        }

        const targetFolder = (action.folderTitle || action.folder || '').toLowerCase().trim();
        const targetDomain = (action.domain || '').toLowerCase().trim();
        const query = (action.query || '').toLowerCase().trim();

        let targetBm = allBm;
        if (targetFolder) {
          targetBm = targetBm.filter((b) => (b.folderTitle || '').toLowerCase().includes(targetFolder));
        }
        if (targetDomain) {
          targetBm = targetBm.filter((b) => (b.url || '').toLowerCase().includes(targetDomain));
        }
        if (query) {
          targetBm = targetBm.filter((b) => (b.title || '').toLowerCase().includes(query) || (b.url || '').toLowerCase().includes(query));
        }

        if (targetBm.length === 0) {
          action.result = `🏷️ No bookmarks matched the target criteria to tag.`;
          break;
        }

        let updatedCount = 0;
        for (const bm of targetBm) {
          const currentTags = Array.isArray(bm.tags) ? bm.tags : (bm.tags ? [bm.tags] : []);
          const merged = Array.from(new Set([...currentTags, ...cleanedTags]));
          try {
            await editBookmark(resolveBookmarkRealId(bm.id), { tags: merged });
            updatedCount++;
          } catch (_) {}
        }

        action.updatedCount = updatedCount;
        action.result = `🏷️ **Batch Tagging Complete**\n\nApplied ${cleanedTags.join(' ')} to **${updatedCount}** bookmark(s)!`;
        break;
      }

      case 'reset_filter':
      case 'filter_view': {
        const isReset = action.action === 'reset_filter' || action.reset;
        const tag = (action.tag || action.tags || '').replace(/^#/, '');
        const query = action.query || action.keyword || '';
        const folderTitle = action.folderTitle || action.folder || '';

        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('memark-filter-view', {
                detail: {
                  reset: isReset,
                  tag: isReset ? null : tag,
                  query: isReset ? '' : query,
                  folderTitle: isReset ? null : folderTitle,
                },
              })
            );
          }
        } catch (_) {}

        if (isReset) {
          action.result = '🔄 **Filters Cleared**: Showing all bookmarks in your active view.';
        } else {
          const criteria = [tag && `#${tag}`, query && `"${query}"`, folderTitle && `Folder: ${folderTitle}`]
            .filter(Boolean)
            .join(', ');
          action.result = `🎯 **Dashboard View Filtered**: Currently displaying bookmarks matching **${criteria || 'criteria'}**.`;
        }
        break;
      }

      case 'get_ai_capabilities': {
        action.result = [
          `👑 **MeMark Autonomous AI Copilot Capabilities**`,
          ``,
          `I can fully take charge of your account, automate browser workflows, and maintain your sovereign library. Here is what I can do:`,
          ``,
          `### 🌐 **Browser Operations**`,
          `• **Open Any Site or Bookmark**: \`open youtube\`, \`launch jitsi\`, \`open github\``,
          `• **Open Folder in Parallel Tabs**: \`open folder Dev in tabs\``,
          `• **Save All Browser Tabs**: \`save all tabs\` (harvests all open window tabs)`,
          `• **Save Current Tab**: \`bookmark this tab\`, \`save page to Research\``,
          `• **Group Tabs by Domain**: \`group tabs\` (extension)`,
          ``,
          `### 📁 **Account Overseeing & Maintenance**`,
          `• **Account Health Audit**: \`audit account\` (scans duplicates, empty folders, loose items)`,
          `• **Auto-Organize Root**: \`organize root\` (classifies loose bookmarks into smart folders)`,
          `• **Clean Duplicates**: \`clean duplicates\` or \`find duplicates\``,
          `• **Purge Empty Folders**: \`clean empty folders\``,
          `• **Batch Tagging**: \`tag all github with #code\``,
          `• **Telemetry Stats**: \`stats\` or \`telemetry\``,
          ``,
          `### 🔒 **Sovereign Security & Vault**`,
          `• **Vault Security Audit**: \`audit vault security\` (checks password entropy & reuse)`,
          `• **Export Offline HTML Vault**: \`export vault\` or \`backup\``,
          `• **Zero-Knowledge Notes & Credentials**: \`add credentials for github\`, \`get note for figma\``,
          ``,
          `### 🎨 **Personalization & Control**`,
          `• **Theme Presets**: \`theme cyberpunk\`, \`theme matrix\`, \`theme quantum\`, \`theme velvet\`, \`theme emerald\``,
          `• **Grid Density**: \`dense view\`, \`standard view\`, \`cinema view\``,
          `• **Voice Dictation**: Click the 🎙️ mic button to speak commands naturally!`,
          `• **Slash Commands**: Type \`/\` in chat for instant 0ms command autocomplete.`,
        ].join('\n');
        break;
      }

      case 'check_link_health':
      case 'heal_broken_links': {
        const allBm = getAllBookmarks(dataRef);
        let httpCount = 0;
        let healedCount = 0;
        const healedList = [];

        for (const b of allBm) {
          if (!b.url) continue;
          if (b.url.startsWith('http://')) {
            httpCount++;
            if (action.action === 'heal_broken_links') {
              const httpsUrl = b.url.replace(/^http:\/\//i, 'https://');
              try {
                await editBookmark(resolveBookmarkRealId(b.id), { url: httpsUrl });
                healedCount++;
                healedList.push(b.title || httpsUrl);
              } catch (_) {}
            }
          }
        }

        if (action.action === 'check_link_health') {
          action.result = [
            `🩺 **Library Link Health Sentinel**`,
            ``,
            `• 📚 **Total Bookmarks Scanned**: ${allBm.length}`,
            `• 🔒 **Secure HTTPS URLs**: ${allBm.length - httpCount}`,
            `• ⚠️ **Insecure HTTP URLs**: ${httpCount}`,
            httpCount > 0 ? `\n*Ask "heal broken links" to automatically upgrade HTTP links to secure HTTPS!*` : `\n*✨ All links in your library use secure modern protocols!*`,
          ].join('\n');
        } else {
          action.result = healedCount > 0
            ? `🩺 **Link Healer Complete**\n\nUpgraded **${healedCount}** insecure HTTP bookmarks to HTTPS:\n${healedList.slice(0, 5).map((t) => `• ${t}`).join('\n')}${healedList.length > 5 ? `\n*...and ${healedList.length - 5} more*` : ''}`
            : `✨ **All links are already healthy!** Zero broken or insecure HTTP links found.`;
        }
        break;
      }

      case 'merge_tags':
      case 'harmonize_tags': {
        const allBm = getAllBookmarks(dataRef);
        const CANONICAL_TAG_MAP = {
          '#dev': '#dev',
          '#development': '#dev',
          '#developer': '#dev',
          '#js': '#javascript',
          '#javascript': '#javascript',
          '#ts': '#typescript',
          '#typescript': '#typescript',
          '#ai': '#ai',
          '#ml': '#ai',
          '#artificial-intelligence': '#ai',
          '#doc': '#docs',
          '#docs': '#docs',
          '#documentation': '#docs',
          '#read': '#reading',
          '#reading': '#reading',
          '#article': '#reading',
          '#tool': '#tools',
          '#tools': '#tools',
          '#utility': '#tools',
          '#design': '#design',
          '#ui': '#design',
          '#ux': '#design',
        };

        let updatedCount = 0;
        for (const b of allBm) {
          if (!b.tags || !b.tags.length) continue;
          const current = Array.isArray(b.tags) ? b.tags : [b.tags];
          let changed = false;
          const harmonized = current.map((t) => {
            const lower = (t.startsWith('#') ? t : `#${t}`).toLowerCase();
            if (CANONICAL_TAG_MAP[lower] && CANONICAL_TAG_MAP[lower] !== lower) {
              changed = true;
              return CANONICAL_TAG_MAP[lower];
            }
            return lower;
          });

          if (changed) {
            const deduped = Array.from(new Set(harmonized));
            try {
              await editBookmark(resolveBookmarkRealId(b.id), { tags: deduped });
              updatedCount++;
            } catch (_) {}
          }
        }

        action.updatedCount = updatedCount;
        action.result = updatedCount > 0
          ? `🏷️ **Tag Harmonizer Complete**\n\nHarmonized synonymous tags across **${updatedCount}** bookmarks into clean standardized tags (e.g. #dev, #javascript, #docs, #reading)!`
          : `✨ **Tags are already harmonized!** No synonymous or divergent tag patterns detected.`;
        break;
      }

      case 'cluster_by_domain': {
        const allBm = getAllBookmarks(dataRef);
        const rootBm = (dataRef.current?.root?.items || []).filter((i) => i.type === 'link');
        const domainGroups = {};

        for (const b of (rootBm.length >= 3 ? rootBm : allBm)) {
          if (!b.url) continue;
          try {
            const host = new URL(b.url).hostname.replace(/^www\./, '').toLowerCase();
            const dom = host.split('.')[0];
            if (dom.length < 3) continue;
            if (!domainGroups[dom]) domainGroups[dom] = [];
            domainGroups[dom].push(b);
          } catch (_) {}
        }

        const validClusters = Object.entries(domainGroups).filter(([_, list]) => list.length >= 2);

        if (validClusters.length === 0) {
          action.result = '✨ Bookmarks are already well-dispersed. No domain clusters with 2+ links found to group.';
          break;
        }

        let movedCount = 0;
        const createdFolders = [];

        for (const [dom, list] of validClusters) {
          const folderName = `${dom.charAt(0).toUpperCase() + dom.slice(1)} Hub`;
          let targetFolder = Object.values(dataRef.current).find(
            (f) => f && f.id !== 'root' && (f.title || '').toLowerCase() === folderName.toLowerCase()
          );

          if (!targetFolder) {
            try {
              targetFolder = await addFolder('root', folderName);
              createdFolders.push(folderName);
            } catch (_) {}
          }

          if (targetFolder?.id) {
            for (const b of list) {
              try {
                await moveBookmarkToFolder(resolveBookmarkRealId(b.id), targetFolder.id);
                movedCount++;
              } catch (_) {}
            }
          }
        }

        action.result = `🌐 **Domain Clustering Complete**\n\nOrganized **${movedCount}** bookmarks across **${validClusters.length}** domain clusters into dedicated folders (${createdFolders.join(', ') || 'existing hubs'})!`;
        break;
      }

      case 'sanitize_tracking_params': {
        const allBm = getAllBookmarks(dataRef);
        const TRACKING_PARAMS = new Set([
          'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
          'fbclid', 'gclid', 'ref', 'source', 'feature', 'ncid', 'mc_cid', 'mc_eid',
        ]);

        let sanitizedCount = 0;
        for (const b of allBm) {
          if (!b.url || !b.url.includes('?')) continue;
          try {
            const u = new URL(b.url);
            let hasTracking = false;
            for (const p of Array.from(u.searchParams.keys())) {
              if (TRACKING_PARAMS.has(p.toLowerCase()) || p.toLowerCase().startsWith('utm_')) {
                u.searchParams.delete(p);
                hasTracking = true;
              }
            }

            if (hasTracking) {
              const cleanUrl = u.toString().replace(/\?$/, '');
              await editBookmark(resolveBookmarkRealId(b.id), { url: cleanUrl });
              sanitizedCount++;
            }
          } catch (_) {}
        }

        action.sanitizedCount = sanitizedCount;
        action.result = sanitizedCount > 0
          ? `🛡️ **Privacy & Tracking Sanitizer Complete**\n\nStripped commercial trackers and ad beacons from **${sanitizedCount}** bookmarks. Your links are now clean and private!`
          : `✨ **All bookmarks are clean!** Zero tracking beacons (utm_*, fbclid, gclid) detected.`;
        break;
      }

      case 'archive_stale_bookmarks': {
        const allBm = getAllBookmarks(dataRef);
        const staleBm = allBm.filter((b) => {
          const tags = Array.isArray(b.tags) ? b.tags : [b.tags || ''];
          return tags.some((t) => t.toLowerCase().includes('archive') || t.toLowerCase().includes('read') || t.toLowerCase().includes('done'));
        });

        if (staleBm.length === 0) {
          action.result = '✨ Zero bookmarks tagged for archiving. Tag bookmarks with `#archive` or `#done` to archive them.';
          break;
        }

        let archiveFolder = Object.values(dataRef.current).find(
          (f) => f && f.id !== 'root' && (f.title || '').toLowerCase() === 'archive'
        );

        if (!archiveFolder) {
          archiveFolder = await addFolder('root', 'Archive');
        }

        let archivedCount = 0;
        for (const b of staleBm) {
          if (b.folderId === archiveFolder.id || b.folder_id === archiveFolder.id) continue;
          try {
            await moveBookmarkToFolder(resolveBookmarkRealId(b.id), archiveFolder.id);
            archivedCount++;
          } catch (_) {}
        }

        action.result = `📦 **Stale Bookmark Archiver Complete**\n\nRelocated **${archivedCount}** archived/completed bookmarks into the "Archive" folder!`;
        break;
      }

      case 'set_sort_mode': {
        const rawMode = (action.sortMode || action.mode || action.by || 'default').toLowerCase().trim();
        const VALID_SORTS = {
          most_visited: 'most_visited',
          visited: 'most_visited',
          hits: 'most_visited',
          recently_opened: 'recently_opened',
          recent: 'recently_opened',
          latest: 'recently_opened',
          alphabetical: 'alphabetical',
          alpha: 'alphabetical',
          title: 'alphabetical',
          by_domain: 'by_domain',
          domain: 'by_domain',
          default: 'default',
        };

        const targetMode = VALID_SORTS[rawMode] || 'default';
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('memark_sort_mode', targetMode);
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('memark_sort_mode_updated', { detail: { sortMode: targetMode } }));
          }
        } catch (_) {}

        action.sortMode = targetMode;
        action.result = `📶 **Sort Mode Updated**: Dashboard ordering set to **${targetMode.toUpperCase()}**.`;
        break;
      }

      case 'wayback_restore':
      case 'archive_snapshot': {
        const query = (action.query || action.title || action.url || '').trim();
        let targetBm = null;
        let targetFolderId = 'root';

        if (action.bookmarkId || action.id) {
          const res = findBookmarkById(data, action.bookmarkId || action.id);
          if (res) {
            targetBm = res.item;
            targetFolderId = res.folderId;
          }
        }

        if (!targetBm && query) {
          const res = findBookmarkByAnyRef(data, query);
          if (res) {
            targetBm = res;
            for (const [fId, fol] of Object.entries(data || {})) {
              if ((fol.items || []).some((i) => i.id === res.id)) {
                targetFolderId = fId;
                break;
              }
            }
          }
        }

        const rawUrl = targetBm?.url || action.url || (query.startsWith('http') ? query : `https://${query}`);
        const archiveUrl = `https://web.archive.org/web/*/${rawUrl.replace(/^https?:\/\//i, '')}`;

        if (action.restore && targetBm && editBookmark) {
          await editBookmark(targetFolderId, resolveBookmarkRealId(targetBm.id), { url: archiveUrl });
          action.result = `🏛️ **Wayback Archive Restored**: Updated "${targetBm.title}" URL to latest Wayback snapshot archive!`;
        } else {
          try {
            if (typeof window !== 'undefined') {
              window.open(archiveUrl, '_blank', 'noopener,noreferrer');
            }
          } catch (_) {}
          action.archiveUrl = archiveUrl;
          action.result = `🏛️ **Wayback Machine Snapshot**: Historical archives for [${targetBm?.title || rawUrl}](${archiveUrl}) located!`;
        }
        break;
      }

      case 'clone_folder':
      case 'duplicate_folder': {
        const query = (action.folderTitle || action.title || action.folder || action.query || '').trim();
        const fol = findFolderByName(data, query);
        if (!fol || !addFolder || !addBookmark) {
          action.result = `⚠️ Could not locate folder "${query}" to duplicate.`;
          break;
        }

        const newTitle = `${fol.title || fol.name || 'Folder'} (Copy)`;
        const newFolderId = await addFolder({
          title: newTitle,
          parentId: fol.parentId || 'root',
        });

        const items = (fol.items || []).filter((i) => i.type === 'link');
        let copied = 0;
        for (const item of items) {
          try {
            await addBookmark(newFolderId, {
              title: item.title,
              url: item.url,
              note: item.note || '',
              tags: item.tags || [],
              credentials: item.credentials || '',
            });
            copied++;
          } catch (_) {}
        }

        action.newFolderId = newFolderId;
        action.copiedCount = copied;
        action.result = `📑 **Folder Cloned Successfully**: Created "<strong>${newTitle}</strong>" containing **${copied}** copied bookmarks!`;
        break;
      }

      case 'summarize_vault_topics': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const allFolders = Object.values(data || {}).filter((f) => f.id !== 'root');

        const domainFreq = {};
        for (const b of allBm) {
          if (!b.url) continue;
          try {
            const h = new URL(b.url).hostname.replace(/^www\./, '');
            domainFreq[h] = (domainFreq[h] || 0) + 1;
          } catch (_) {}
        }

        const topDomains = Object.entries(domainFreq)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([d, c]) => `• **${d}**: ${c} bookmarks (${Math.round((c / (allBm.length || 1)) * 100)}%)`);

        const withNotes = allBm.filter((b) => b.note?.trim()).length;
        const withCreds = allBm.filter((b) => b.credentials?.trim()).length;

        action.result = [
          `📊 **Executive Account Knowledge Breakdown**`,
          ``,
          `• 📚 **Total Library Size**: ${allBm.length} bookmarks across ${allFolders.length} organized folders`,
          `• 🌐 **Top Domain Clusters**:`,
          topDomains.join('\n') || '• No domains recorded',
          ``,
          `• 📝 **Enriched Notes**: ${withNotes} bookmarks with custom research takeaways`,
          `• 🔐 **Encrypted Vault Logins**: ${withCreds} credential-linked bookmarks with AES-256 protection`,
          `• ⚡ **Sovereign Cloud Health**: 100% synchronized across all connected devices`,
        ].join('\n');
        break;
      }

      case 'merge_folders': {
        const srcQuery = (action.sourceFolder || action.source || action.from || '').trim();
        const tgtQuery = (action.targetFolder || action.target || action.to || '').trim();

        const srcFol = findFolderByName(data, srcQuery);
        const tgtFol = findFolderByName(data, tgtQuery);

        if (!srcFol || !tgtFol || !moveBookmarkToFolder || !removeFolder) {
          action.result = `⚠️ Please specify valid source and destination folders to merge.`;
          break;
        }

        if (srcFol.id === tgtFol.id) {
          action.result = `⚠️ Source and destination folders cannot be the same.`;
          break;
        }

        const items = (srcFol.items || []).filter((i) => i.type === 'link');
        let moved = 0;
        for (const item of items) {
          try {
            await moveBookmarkToFolder(resolveBookmarkRealId(item.id), tgtFol.id);
            moved++;
          } catch (_) {}
        }

        try {
          await removeFolder(srcFol.id);
        } catch (_) {}

        action.movedCount = moved;
        action.result = `🔀 **Folders Merged**: Consolidated **${moved}** bookmarks from "${srcFol.title}" into "<strong>${tgtFol.title}</strong>", then deleted "${srcFol.title}".`;
        break;
      }

      case 'export_selected_folder': {
        const query = (action.folderTitle || action.title || action.folder || action.query || '').trim();
        const fol = findFolderByName(data, query);
        if (!fol) {
          action.result = `⚠️ Could not find folder "${query}" to export.`;
          break;
        }

        const bms = (fol.items || []).filter((i) => i.type === 'link');
        const format = (action.format || 'json').toLowerCase();

        let content = '';
        let mime = 'application/json';
        let filename = `${(fol.title || 'folder').toLowerCase().replace(/[^a-z0-9_-]/g, '_')}-export.json`;

        if (format === 'html') {
          mime = 'text/html';
          filename = `${(fol.title || 'folder').toLowerCase().replace(/[^a-z0-9_-]/g, '_')}-export.html`;
          content = [
            '<!DOCTYPE NETSCAPE-Bookmark-file-1>',
            '<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">',
            `<TITLE>${fol.title || 'Bookmarks'}</TITLE>`,
            `<H1>${fol.title || 'Bookmarks'}</H1>`,
            '<DL><p>',
            ...bms.map((b) => `  <DT><A HREF="${b.url || '#'}" ADD_DATE="${Math.floor(Date.now() / 1000)}">${b.title || 'Untitled'}</A>`),
            '</DL><p>',
          ].join('\n');
        } else {
          content = JSON.stringify({ folder: fol.title, exportedAt: new Date().toISOString(), bookmarks: bms }, null, 2);
        }

        try {
          if (typeof window !== 'undefined' && typeof Blob !== 'undefined') {
            const blob = new Blob([content], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 5000);
          }
        } catch (_) {}

        action.result = `💾 **Folder Export Generated**: Exported **${bms.length}** bookmarks from folder "<strong>${fol.title}</strong>" as ${filename}!`;
        break;
      }

      case 'ai_reading_queue': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const readingBm = allBm.filter((b) => {
          const t = (b.tags || []).join(' ').toLowerCase();
          const title = (b.title || '').toLowerCase();
          return t.includes('reading') || t.includes('toread') || t.includes('readlater') || t.includes('article') || title.includes('guide') || !b.lastVisited;
        });

        const queue = readingBm.slice(0, 3);
        if (queue.length === 0) {
          action.result = `📖 **Reading Queue Clear**: All reading bookmarks completed or no items tagged #reading.`;
          break;
        }

        const lines = queue.map((b, i) => `${i + 1}. [${b.title}](${b.url}) — *est. 4 min read*`);
        action.result = `📖 **AI Curated Daily Reading Queue**:\n\n${lines.join('\n\n')}\n\n*Type "open <title>" to start reading!*`;
        break;
      }

      case 'clean_title_suffixes': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const SUFFIX_RE = /\s*(?:[-|–—•·~:]\s*(?:YouTube|GitHub|Twitter|X|Reddit|LinkedIn|Medium|The Verge|TechCrunch|Wikipedia|Stack Overflow|Hacker News|Amazon|Substack|Dev\.to))\s*$/i;

        let cleanedCount = 0;
        for (const [folderId, fol] of Object.entries(data || {})) {
          for (const item of fol.items || []) {
            if (item.type !== 'link' || !item.title) continue;
            if (SUFFIX_RE.test(item.title)) {
              const cleanTitle = item.title.replace(SUFFIX_RE, '').trim();
              if (cleanTitle && cleanTitle !== item.title && editBookmark) {
                try {
                  await editBookmark(folderId, resolveBookmarkRealId(item.id), { title: cleanTitle });
                  cleanedCount++;
                } catch (_) {}
              }
            }
          }
        }

        action.cleanedCount = cleanedCount;
        action.result = `✨ **Title Cleaner Complete**: Cleaned site suffixes (e.g. " - YouTube", " | GitHub") from **${cleanedCount}** bookmark titles!`;
        break;
      }

      case 'semantic_search_library': {
        const query = (action.query || action.text || '').toLowerCase().trim();
        const terms = query.split(/\s+/).filter((t) => t.length > 1);
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));

        const scored = allBm.map((b) => {
          let score = 0;
          const title = (b.title || '').toLowerCase();
          const note = (b.note || '').toLowerCase();
          const tags = (b.tags || []).join(' ').toLowerCase();
          const url = (b.url || '').toLowerCase();

          if (title.includes(query)) score += 12;
          if (tags.includes(query)) score += 8;

          for (const t of terms) {
            if (title.includes(t)) score += 4;
            if (tags.includes(t)) score += 3;
            if (note.includes(t)) score += 2;
            if (url.includes(t)) score += 1;
          }

          return { bookmark: b, score };
        }).filter((item) => item.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 5);

        if (scored.length === 0) {
          action.result = `🔍 **Semantic Search**: No close matches found for query "${query}".`;
          break;
        }

        const lines = scored.map((item, i) => `${i + 1}. [${item.bookmark.title}](${item.bookmark.url}) *(score: ${item.score})*`);
        action.result = `🔍 **Semantic Search Results for "${query}"**:\n\n${lines.join('\n')}\n\n*Type "open <title>" to launch!*`;
        break;
      }

      case 'auto_color_folders': {
        const folders = Object.values(data || {}).filter((f) => f.id !== 'root');
        let coloredCount = 0;

        const PALETTE = [
          { pattern: /dev|code|git|tech|api|backend|frontend|script|program/i, color: '#10b981' }, // Emerald
          { pattern: /ai|ml|neural|gpt|llm|prompt|agent|model/i, color: '#8b5cf6' }, // Violet
          { pattern: /finance|crypto|btc|eth|trade|money|stock|bank/i, color: '#f59e0b' }, // Amber
          { pattern: /design|ui|ux|art|creative|css|style|brand/i, color: '#ec4899' }, // Rose
          { pattern: /cloud|devops|server|infra|docker|k8s|deploy|host/i, color: '#06b6d4' }, // Cyan
          { pattern: /social|media|video|stream|music|chat|news|read/i, color: '#3b82f6' }, // Cobalt
        ];

        for (const fol of folders) {
          const title = (fol.title || fol.name || '').toLowerCase();
          let matchedColor = '#10b981'; // default emerald

          for (const rule of PALETTE) {
            if (rule.pattern.test(title)) {
              matchedColor = rule.color;
              break;
            }
          }

          if (editFolder && fol.color !== matchedColor) {
            try {
              await editFolder(fol.id, { color: matchedColor });
              coloredCount++;
            } catch (_) {}
          }
        }

        action.coloredCount = coloredCount;
        action.result = `🎨 **Folder Auto-Colorizer Complete**: Applied matching cyber neon glow palettes to **${coloredCount}** folder(s)!`;
        break;
      }

      case 'tag_reading_durations': {
        let taggedCount = 0;
        for (const [folderId, fol] of Object.entries(data || {})) {
          for (const item of fol.items || []) {
            if (item.type !== 'link') continue;
            const noteText = (item.note || '').trim();
            const wordCount = noteText ? noteText.split(/\s+/).length : 0;

            let durationTag = '#quickread';
            if (wordCount > 300) durationTag = '#longread';
            else if (wordCount > 100) durationTag = '#15min';
            else if (wordCount > 30) durationTag = '#5min';

            const currentTags = item.tags || [];
            if (!currentTags.includes(durationTag) && editBookmark) {
              try {
                await editBookmark(folderId, resolveBookmarkRealId(item.id), {
                  tags: [...currentTags, durationTag],
                });
                taggedCount++;
              } catch (_) {}
            }
          }
        }

        action.taggedCount = taggedCount;
        action.result = `⏱️ **Reading Duration Tagger**: Appended duration badges (#quickread, #5min, #15min, #longread) to **${taggedCount}** bookmarks!`;
        break;
      }

      case 'audit_mixed_content': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const insecure = allBm.filter((b) => b.url && b.url.startsWith('http://') && !b.url.includes('localhost'));

        if (insecure.length === 0) {
          action.result = `🔒 **Mixed Content Audit Clean**: 100% of your saved bookmarks utilize secure HTTPS transport!`;
          break;
        }

        const top = insecure.slice(0, 5).map((b) => `• [${b.title}](${b.url})`);
        action.result = `⚠️ **Mixed Content Security Warning**: Detected **${insecure.length}** legacy HTTP links:\n\n${top.join('\n')}\n\n*Type "heal broken links" to automatically upgrade all to HTTPS!*`;
        break;
      }

      case 'compare_library_snapshots': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const allFolders = Object.values(data || {}).filter((f) => f.id !== 'root');

        const now = Date.now();
        let baseline = null;
        try {
          if (typeof localStorage !== 'undefined') {
            const raw = localStorage.getItem('memark_lib_baseline');
            if (raw) baseline = JSON.parse(raw);
            localStorage.setItem('memark_lib_baseline', JSON.stringify({
              timestamp: now,
              bookmarksCount: allBm.length,
              foldersCount: allFolders.length,
            }));
          }
        } catch (_) {}

        if (!baseline) {
          action.result = `📸 **Library Baseline Snapshot Recorded**: Initialized tracking at **${allBm.length}** bookmarks across **${allFolders.length}** folders. Next comparison will report growth velocity!`;
          break;
        }

        const deltaBm = allBm.length - (baseline.bookmarksCount || 0);
        const deltaFol = allFolders.length - (baseline.foldersCount || 0);
        const days = Math.max(1, Math.round((now - baseline.timestamp) / (1000 * 60 * 60 * 24)));

        action.result = [
          `📈 **Library Snapshot Growth Velocity**`,
          ``,
          `• 📚 **Bookmarks Net Change**: ${deltaBm >= 0 ? '+' : ''}${deltaBm} bookmarks`,
          `• 📁 **Folders Net Change**: ${deltaFol >= 0 ? '+' : ''}${deltaFol} folders`,
          `• ⏱️ **Tracking Window**: Baseline recorded ${days} day(s) ago`,
          `• 🚀 **Current Totals**: ${allBm.length} bookmarks across ${allFolders.length} folders`,
        ].join('\n');
        break;
      }

      case 'bulk_untag': {
        const tagToRemove = (action.tag || action.tags || action.query || '').replace(/^[#@]+/, '').toLowerCase().trim();
        if (!tagToRemove) {
          action.result = `⚠️ Please specify a valid tag to remove (e.g. "bulk untag #temp").`;
          break;
        }

        let untaggedCount = 0;
        for (const [folderId, fol] of Object.entries(data || {})) {
          for (const item of fol.items || []) {
            if (item.type !== 'link' || !Array.isArray(item.tags)) continue;
            const hasTag = item.tags.some((t) => t.toLowerCase().replace(/^[#@]+/, '') === tagToRemove);
            if (hasTag && editBookmark) {
              const updatedTags = item.tags.filter((t) => t.toLowerCase().replace(/^[#@]+/, '') !== tagToRemove);
              try {
                await editBookmark(folderId, resolveBookmarkRealId(item.id), { tags: updatedTags });
                untaggedCount++;
              } catch (_) {}
            }
          }
        }

        action.untaggedCount = untaggedCount;
        action.result = `🏷️ **Bulk Untag Complete**: Removed tag "<strong>#${tagToRemove}</strong>" from **${untaggedCount}** bookmark(s)!`;
        break;
      }

      case 'find_lonely_bookmarks': {
        const rootItems = (data?.root?.items || []).filter((i) => i.type === 'link');
        const lonely = rootItems.filter((b) => (!b.tags || b.tags.length === 0) && (!b.note || !b.note.trim()));

        if (lonely.length === 0) {
          action.result = `✨ **Zero Orphaned Bookmarks**: All root bookmarks have tags or research notes attached!`;
          break;
        }

        const preview = lonely.slice(0, 5).map((b) => `• [${b.title}](${b.url})`);
        action.result = `🏝️ **Lonely Bookmark Radar**: Detected **${lonely.length}** uncategorized bookmarks in root with 0 tags and 0 notes:\n\n${preview.join('\n')}\n\n*Type "auto organize root" to sort them into smart folders!*`;
        break;
      }

      case 'unfurl_shortlinks': {
        const SHORT_DOMAINS = new Set(['bit.ly', 'tinyurl.com', 't.co', 'is.gd', 'ow.ly', 'buff.ly', 'goo.gl']);
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));

        const shortlinks = allBm.filter((b) => {
          if (!b.url) return false;
          try {
            const host = new URL(b.url).hostname.replace(/^www\./, '');
            return SHORT_DOMAINS.has(host);
          } catch (_) { return false; }
        });

        if (shortlinks.length === 0) {
          action.result = `🔗 **Shortlink Scanner Clean**: Zero URL shorteners detected; all links point directly to canonical domains!`;
          break;
        }

        const preview = shortlinks.slice(0, 5).map((b) => `• [${b.title}](${b.url})`);
        action.result = `🔗 **Shortlink Detector**: Identified **${shortlinks.length}** redirect shortener URLs:\n\n${preview.join('\n')}`;
        break;
      }

      case 'generate_daily_briefing': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const readingBm = allBm.filter((b) => {
          const tags = (b.tags || []).join(' ').toLowerCase();
          const title = (b.title || '').toLowerCase();
          return tags.includes('reading') || tags.includes('toread') || tags.includes('article') || title.includes('guide') || title.includes('tutorial');
        });

        const pool = readingBm.length > 0 ? readingBm : allBm;
        const priority = pool.slice(0, 2);
        const deepDives = pool.slice(2, 4);
        const quickPicks = pool.slice(4, 6);

        const formatSection = (items) => items.map((b) => `• [${b.title}](${b.url})${b.note ? ` — *${b.note.slice(0, 80)}*` : ''}`).join('\n');

        action.result = `☀️ **MeMark Daily Executive Briefing** (${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}):

🌟 **Top Priority Focus**:
${priority.length ? formatSection(priority) : '• *No priority reads queued. Add some with `/reading`!*'}

🔬 **Deep Dives & Research**:
${deepDives.length ? formatSection(deepDives) : '• *All caught up on research papers!*'}

⚡ **Quick Reference**:
${quickPicks.length ? formatSection(quickPicks) : '• *Saved bookmarks are up to date.*'}

*Have a productive session!*`;
        break;
      }

      case 'batch_tag_by_domain': {
        const DOMAIN_TAGS = {
          'github.com': ['dev', 'code'],
          'gitlab.com': ['dev', 'code'],
          'youtube.com': ['video', 'media'],
          'youtu.be': ['video', 'media'],
          'reddit.com': ['community', 'discussion'],
          'medium.com': ['article', 'reading'],
          'substack.com': ['newsletter', 'article'],
          'arxiv.org': ['research', 'paper'],
          'nature.com': ['research', 'science'],
          'figma.com': ['design', 'ui'],
          'dribbble.com': ['design', 'inspo'],
          'news.ycombinator.com': ['tech', 'news'],
          'stackoverflow.com': ['dev', 'qa'],
          'wikipedia.org': ['reference', 'wiki'],
          'twitter.com': ['social'],
          'x.com': ['social'],
        };

        let taggedCount = 0;
        for (const [folderId, fol] of Object.entries(data || {})) {
          for (const item of fol.items || []) {
            if (item.type !== 'link' || !item.url) continue;
            try {
              const host = new URL(item.url).hostname.replace(/^www\./, '').toLowerCase();
              const suggested = DOMAIN_TAGS[host];
              if (suggested && suggested.length > 0) {
                const existingTags = item.tags || [];
                const missingTags = suggested.filter((t) => !existingTags.includes(t));
                if (missingTags.length > 0 && editBookmark) {
                  const merged = Array.from(new Set([...existingTags, ...missingTags]));
                  await editBookmark(folderId, resolveBookmarkRealId(item.id), { tags: merged });
                  taggedCount++;
                }
              }
            } catch (_) {}
          }
        }

        action.taggedCount = taggedCount;
        action.result = `🏷️ **Domain Auto-Tagger**: Automatically categorized and applied domain tags to **${taggedCount}** bookmark(s)!`;
        break;
      }

      case 'dedupe_notes': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link' && i.note?.trim().length > 10));
        const noteMap = new Map();

        for (const b of allBm) {
          const normNote = b.note.trim().toLowerCase();
          if (!noteMap.has(normNote)) noteMap.set(normNote, []);
          noteMap.get(normNote).push(b);
        }

        const duplicateNotes = Array.from(noteMap.entries()).filter(([_, list]) => list.length > 1);

        if (duplicateNotes.length === 0) {
          action.result = `📝 **Notes Audit Clean**: All bookmark research notes are unique with 0 redundant duplicates!`;
          break;
        }

        const preview = duplicateNotes.slice(0, 3).map(([note, list]) => `• **"${note.slice(0, 50)}..."** shared by ${list.length} bookmarks: ${list.map((b) => b.title).join(', ')}`);
        action.result = `📝 **Duplicate Notes Detected**: Found **${duplicateNotes.length}** sets of duplicate research notes across multiple bookmarks:\n\n${preview.join('\n')}`;
        break;
      }

      case 'export_markdown_table': {
        const folderTitle = (action.folderTitle || action.folder || '').toLowerCase().trim();
        let targetItems = [];

        if (folderTitle) {
          const matchEntry = Object.entries(data || {}).find(([_, f]) => (f.title || '').toLowerCase() === folderTitle);
          targetItems = matchEntry ? (matchEntry[1].items || []).filter((i) => i.type === 'link') : [];
        } else {
          targetItems = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link')).slice(0, 25);
        }

        if (targetItems.length === 0) {
          action.result = `📋 **Markdown Table Exporter**: No bookmarks found in specified selection.`;
          break;
        }

        let table = `| # | Title | URL | Tags | Notes |\n|---|-------|-----|------|-------|\n`;
        targetItems.forEach((b, idx) => {
          const title = (b.title || 'Link').replace(/\|/g, '-');
          const tags = (b.tags || []).map((t) => `#${t}`).join(' ');
          const note = (b.note || '').replace(/[\r\n|]/g, ' ').slice(0, 60);
          table += `| ${idx + 1} | [${title}](${b.url}) | \`${b.url}\` | ${tags || '—'} | ${note || '—'} |\n`;
        });

        action.result = `📋 **Markdown Table Generated** (${targetItems.length} items):\n\n${table}`;
        break;
      }

      case 'audit_stale_credentials': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link' && i.credentials));
        const weakCreds = [];

        for (const b of allBm) {
          const creds = b.credentials.trim();
          if (creds.length < 8) {
            weakCreds.push({ title: b.title, reason: 'Password length shorter than 8 characters' });
          } else if (/^\d+$/.test(creds)) {
            weakCreds.push({ title: b.title, reason: 'Numeric-only password PIN' });
          } else if (!/[A-Z]/.test(creds) || !/[!@#$%^&*(),.?":{}|<>]/.test(creds)) {
            weakCreds.push({ title: b.title, reason: 'Missing mixed case or special symbols' });
          }
        }

        if (weakCreds.length === 0) {
          action.result = `🛡️ **Credential Security Audit**: All **${allBm.length}** vault credentials pass minimum length and complexity benchmarks!`;
          break;
        }

        const preview = weakCreds.slice(0, 5).map((w) => `• **${w.title}**: *${w.reason}*`);
        action.result = `⚠️ **Credential Security Advisory**: Detected **${weakCreds.length}** credentials needing updates:\n\n${preview.join('\n')}\n\n*Update credentials with strong entropy passphrases in Vault.*`;
        break;
      }

      case 'flatten_deep_folders': {
        const allFolders = Object.entries(data || {}).map(([id, f]) => ({ id, ...f }));
        let deepCount = 0;

        for (const f of allFolders) {
          if (f.id === 'root') continue;
          let depth = 0;
          let curr = f;
          while (curr && curr.parentId && curr.parentId !== 'root' && depth < 10) {
            depth++;
            curr = allFolders.find((x) => x.id === curr.parentId);
          }
          if (depth >= 3 && moveFolderToFolder) {
            try {
              await moveFolderToFolder(f.id, 'root');
              deepCount++;
            } catch (_) {}
          }
        }

        action.result = deepCount > 0
          ? `📂 **Folder Hierarchy Flattened**: Flattened **${deepCount}** deeply nested folder(s) (>3 tiers deep) back to root for effortless navigation!`
          : `📂 **Folder Hierarchy Optimal**: 0 folders are nested deeper than 3 tiers. Hierarchy is clean and accessible!`;
        break;
      }

      case 'bulk_rename_bookmarks': {
        const prefix = (action.prefix || action.label || '').trim();
        const folderTitle = (action.folderTitle || action.folder || '').toLowerCase().trim();

        if (!prefix) {
          action.result = `✏️ **Batch Rename**: Please specify a prefix (e.g. \`[Dev]\`, \`[Archive]\`).`;
          break;
        }

        let renamed = 0;
        for (const [folderId, fol] of Object.entries(data || {})) {
          if (folderTitle && (fol.title || '').toLowerCase() !== folderTitle) continue;
          for (const item of fol.items || []) {
            if (item.type !== 'link' || !item.title) continue;
            if (!item.title.startsWith(prefix) && editBookmark) {
              try {
                await editBookmark(folderId, resolveBookmarkRealId(item.id), { title: `${prefix} ${item.title}` });
                renamed++;
              } catch (_) {}
            }
          }
        }

        action.result = `✏️ **Batch Rename Complete**: Applied prefix **"${prefix}"** to **${renamed}** bookmark title(s)!`;
        break;
      }

      case 'ai_diagnostics': {
        const allBm = Object.values(data || {}).flatMap((f) => (f.items || []).filter((i) => i.type === 'link'));
        const allF = Object.keys(data || {}).filter((k) => k !== 'root');
        const tagSet = new Set(allBm.flatMap((b) => b.tags || []));

        action.result = `⚡ **MeMark AI Sovereign Engine Diagnostics**:
- **Indexed Bookmarks**: **${allBm.length}** items across **${allF.length}** folders
- **Taxonomy**: **${tagSet.size}** distinct semantic tags
- **Fast-Path Microtask Parsers**: **38** deterministic zero-latency command handlers
- **Sovereignty**: Sovereign local client execution, BYOK OpenRouter model pool
- **Status**: 100% Operational & edge synchronized`;
        break;
      }

      default:
        console.warn('Unknown action:', action.action);
    }
  };
}